import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, TextField, Button, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeFilter from '../DateRangeFilter';
import EmployeeSearchField from '../common/EmployeeSearchField';

const LeaveFilters = ({
  searchTerm,
  statusFilter,
  employeeFilter,
  leaveTypeFilter,
  dateRange,
  filterOptions,
  onSearchChange,
  onFilterChange,
  onDateRangeChange,
  onClearFilters,
}) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 200px' },
              minWidth: { xs: '100%', sm: '200px', md: '150px' },
            }}
          >
            <TextField
              fullWidth
              placeholder="Search leave requests..."
              value={searchTerm}
              onChange={onSearchChange}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>

          <Box
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 150px' },
              minWidth: { xs: '100%', sm: '150px', md: '120px' },
            }}
          >
            <Autocomplete
              size="small"
              fullWidth
              value={{ value: statusFilter, label: statusFilter === 'All' ? 'All Statuses' : statusFilter }}
              onChange={(event, newValue) => {
                onFilterChange('status', newValue?.value || 'All');
              }}
              options={[
                { value: 'All', label: 'All Statuses' },
                ...filterOptions.statuses.map((status) => ({
                  value: status.status,
                  label: status.status,
                })),
              ]}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => <TextField {...params} label="Status" />}
              clearOnEscape
            />
          </Box>

          <Box
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 180px' },
              minWidth: { xs: '100%', sm: '180px', md: '150px' },
            }}
          >
            <EmployeeSearchField
              value={employeeFilter}
              emptyValue="All"
              onChange={(id) => onFilterChange('employee', id)}
              label="Employee"
            />
          </Box>

          <Box
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 150px' },
              minWidth: { xs: '100%', sm: '150px', md: '120px' },
            }}
          >
            <Autocomplete
              size="small"
              fullWidth
              value={{ value: leaveTypeFilter, label: leaveTypeFilter === 'All' ? 'All Types' : leaveTypeFilter }}
              onChange={(event, newValue) => {
                onFilterChange('leaveType', newValue?.value || 'All');
              }}
              options={[
                { value: 'All', label: 'All Types' },
                ...filterOptions.leave_types.map((type) => ({
                  value: type.leave_type,
                  label: type.leave_type,
                })),
              ]}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => <TextField {...params} label="Leave Type" />}
              clearOnEscape
            />
          </Box>

          <Box
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 100%', md: dateRange.preset === 'custom' ? '1 1 500px' : '1 1 200px' },
              minWidth: { xs: '100%', sm: '100%', md: dateRange.preset === 'custom' ? '500px' : '200px' },
            }}
          >
            <DateRangeFilter
              onDateRangeChange={onDateRangeChange}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              selectedPreset={dateRange.preset}
              sx={{ height: '40px' }}
            />
          </Box>

          <Box
            sx={{
              flex: { xs: '1 1 100%', sm: '0 0 auto', md: '0 0 auto' },
              minWidth: { xs: '100%', sm: 'auto', md: 'auto' },
            }}
          >
            <Button variant="outlined" onClick={onClearFilters} size="small" sx={{ height: '40px', minWidth: '120px' }}>
              Clear Filters
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

LeaveFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  statusFilter: PropTypes.string.isRequired,
  employeeFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leaveTypeFilter: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    preset: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.shape({
    statuses: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string.isRequired,
      })
    ).isRequired,
    employees: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        full_name: PropTypes.string.isRequired,
      })
    ).isRequired,
    leave_types: PropTypes.arrayOf(
      PropTypes.shape({
        leave_type: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

export default LeaveFilters;
